import React, {useEffect, useRef} from 'react';

import Group from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {INITIAL_DOMAIN_DATA, areDomainsValid} from '../../utils/domain-validation';
import {DomainManager} from '../domain-manager/domain-manager';
import {StepFooter} from '../step-footer/step-footer';

import {useAccountDomains} from './hooks/use-account-domains';


import {domainCard, verifyDomainContainer} from './verify-domain.module.styl';

import type {VerifyDomainData} from '../../utils/domain-validation';

type VerifyDomainProps = {
    onContinue: () => void,
    onBack: () => void,
    saving?: boolean,
};

export const VerifyDomain: React.FC<VerifyDomainProps> = ({onContinue, onBack, saving}) => {
    const [domainData, setDomainData] = useSsoSessionStorage<VerifyDomainData>('verifyDomain', INITIAL_DOMAIN_DATA);
    const {fetchDomains, loading: fetchingAccountDomains} = useAccountDomains();
    const fetchAttemptedRef = useRef(false);

    useEffect(() => {
        if (fetchAttemptedRef.current) {
            return;
        }
        fetchAttemptedRef.current = true;

        if (domainData.domains.length > 0) {
            return;
        }

        fetchDomains().then(presetDomains => {
            if (presetDomains.length > 0) {
                setDomainData({domains: presetDomains});
            }
        });
    }, [domainData.domains.length, fetchDomains, setDomainData]);

    const canContinue = areDomainsValid(domainData.domains);

    return (
        <Group direction="v" gap="l" className={verifyDomainContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    Verify Your Domains
                </Typography>
                <Typography variant="body-level-1">
                    Confirm ownership of the email domains that will use SSO.
                </Typography>
            </Group>

            <div className={domainCard}>
                <Typography variant="heading-level-3">
                    Email Domains
                </Typography>
                <DomainManager
                    domainData={domainData}
                    onDomainDataChange={setDomainData}
                    busy={fetchingAccountDomains}
                />
            </div>

            <StepFooter
                onContinue={onContinue}
                onBack={onBack}
                continueDisabled={!canContinue}
                busy={saving}
            />
        </Group>
    );
};
